import type { StateStorage } from 'zustand/middleware';

/**
 * 上传任务的持久化存储适配器。
 *
 * 为什么需要节流：上传进度是高频事件（每个分片一次），而 zustand persist 在每次
 * `set()` 后都会整包 `JSON.stringify` + 写 localStorage。上百个文件的文件夹任务
 * 会产生每秒几十次的全量写入，直接把主线程写卡。这里把写入合并到 1 次/秒，
 * 并在页面隐藏 / 卸载时立即冲刷，保证「强关浏览器」也不会丢太多进度。
 */

/** 合并窗口（毫秒），测试里可以调成 0 */
let throttleMs = 1000;
export const setUploadTaskPersistThrottle = (ms: number) => {
  throttleMs = ms;
};

/** 待写入的最新值（按 key 合并，只保留最后一次） */
const pending = new Map<string, string>();
let timer: ReturnType<typeof setTimeout> | null = null;

/** 立即把挂起的写入落盘 */
export const flushUploadTaskStorage = () => {
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
  pending.forEach((value, key) => {
    try {
      localStorage.setItem(key, value);
    } catch (error) {
      // 配额写满 / 隐私模式下写入失败不应影响上传主流程
      console.warn('[material-upload] 持久化上传任务失败', error);
    }
  });
  pending.clear();
};

const scheduleFlush = () => {
  if (throttleMs <= 0) {
    flushUploadTaskStorage();
    return;
  }
  if (timer) return;
  timer = setTimeout(() => {
    timer = null;
    flushUploadTaskStorage();
  }, throttleMs);
};

/** 仅供测试：丢弃挂起的写入，避免用例间串数据 */
export const resetUploadTaskStorageForTest = () => {
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
  pending.clear();
};

export const throttledUploadTaskStorage: StateStorage = {
  getItem: (name) => {
    // 有未落盘的更新时以内存中的为准，避免读到旧值
    return pending.get(name) ?? localStorage.getItem(name);
  },
  setItem: (name, value) => {
    pending.set(name, value);
    scheduleFlush();
  },
  removeItem: (name) => {
    pending.delete(name);
    localStorage.removeItem(name);
  },
};

/**
 * 注册「页面隐藏 / 卸载时冲刷」的兜底。
 * 放在模块级，保证只注册一次；与 `UploadTaskHost` 的 beforeunload 守卫相互独立。
 */
export const registerUploadTaskStorageFlush = () => {
  if (typeof window === 'undefined') return;
  window.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushUploadTaskStorage();
  });
  window.addEventListener('beforeunload', flushUploadTaskStorage);
};
